import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { ClipboardCheck, GraduationCap, ShoppingBag, Calendar, Clock, User } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, delay: i * 0.1 },
  }),
};

export default function Dashboard() {
  const { user, apiClient } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBookings = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/api/bookings');
      setBookings(data);
    } catch {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const serviceLabels = {
    autism_eval: 'Autism Evaluation',
    neuropsych_eval: 'Neuropsych Evaluation',
    therapy: 'Therapy Session',
    mini_iop: 'Mini-IOP',
  };

  return (
    <div className="min-h-screen pt-16 bg-[var(--bg-alt)]" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Welcome */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[var(--primary-blue)] flex items-center justify-center">
              <User size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-bold" data-testid="user-name">
                Welcome, {user?.name}
              </h2>
              <p className="text-sm text-[var(--text-body)] capitalize">{user?.role?.replace('_', ' ')} Account</p>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {[
            { to: '/book-evaluation', icon: ClipboardCheck, label: 'Book Evaluation', desc: 'Schedule a new evaluation', color: 'blue' },
            { to: '/mini-iop', icon: GraduationCap, label: 'Mini-IOP', desc: 'Enroll in intensive program', color: 'lime' },
            { to: '/shop', icon: ShoppingBag, label: 'Digital Shop', desc: 'Browse resources', color: 'blue' },
          ].map((item, i) => (
            <motion.div key={item.to} variants={fadeUp} initial="hidden" animate="visible" custom={i}>
              <Link to={item.to} className="card-elevated p-5 flex items-center gap-4 block" data-testid={`dashboard-${item.label.toLowerCase().replace(/\s+/g, '-')}-link`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.color === 'lime' ? 'bg-lime-50' : 'bg-blue-50'}`}>
                  <item.icon size={18} className={item.color === 'lime' ? 'text-lime-600' : 'text-[var(--primary-blue)]'} />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-sm">{item.label}</h3>
                  <p className="text-xs text-[var(--text-body)]">{item.desc}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Clinician Training Link */}
        {user?.role === 'clinician' && (
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3} className="mb-10">
            <Link to="/training" className="card-elevated p-5 flex items-center gap-4 block border-l-4 border-[var(--primary-blue)]">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <GraduationCap size={18} className="text-[var(--primary-blue)]" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-sm">Clinician Training Portal</h3>
                <p className="text-xs text-[var(--text-body)]">Access training programs and courses</p>
              </div>
            </Link>
          </motion.div>
        )}

        {user?.role === 'admin' && (
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3} className="mb-10">
            <Link to="/admin" className="card-elevated p-5 flex items-center gap-4 block border-l-4 border-emerald-500" data-testid="admin-panel-link">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <ClipboardCheck size={18} className="text-emerald-600" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-sm">Admin Panel</h3>
                <p className="text-xs text-[var(--text-body)]">Manage bookings, courses, products, and more</p>
              </div>
            </Link>
          </motion.div>
        )}

        {/* Bookings */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4}>
          <div className="card-elevated p-6" data-testid="bookings-section">
            <div className="flex items-center gap-3 mb-6">
              <Calendar size={20} className="text-[var(--primary-blue)]" />
              <h3 className="text-xl font-heading font-bold">My Bookings</h3>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary-blue)]" />
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-12">
                <Clock size={32} className="text-[var(--border-color)] mx-auto mb-3" />
                <p className="text-[var(--text-body)] mb-4">No bookings yet</p>
                <Link to="/book-evaluation" className="btn-primary text-sm !px-6 !py-2 inline-block">
                  Book Your First Evaluation
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {bookings.map((booking) => (
                  <div key={booking.id} className="p-4 rounded-xl bg-[var(--bg-alt)] border border-[var(--border-color)] flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`booking-${booking.id}`}>
                    <div>
                      <p className="font-heading font-bold text-sm">
                        {serviceLabels[booking.service_type] || booking.service_type}
                      </p>
                      <p className="text-xs text-[var(--text-body)]">
                        Patient: {booking.patient_name} &middot; Age: {booking.patient_age}
                      </p>
                      <p className="text-xs text-[var(--text-body)]">
                        {new Date(booking.date).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold self-start ${
                      booking.status === 'confirmed'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {booking.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
